import { useCallback, useEffect, useState } from 'react';
import { Copy, HeartPulse, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'react-toastify';
import type { ChatwootChannel, ChatwootInboundEventSummary } from '../types';
import { chatwootAdminApi } from '../api/chatwoot.api';

type ChatwootChannelDetailDialogProps = {
  channel: ChatwootChannel | null;
  botLabel: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChannelUpdated: () => void;
};

async function copyText(value: string, label: string): Promise<void> {
  await navigator.clipboard.writeText(value);
  toast.success(`${label} copiado`);
}

export function ChatwootChannelDetailDialog({
  channel,
  botLabel,
  open,
  onOpenChange,
  onChannelUpdated,
}: ChatwootChannelDetailDialogProps) {
  const [events, setEvents] = useState<ChatwootInboundEventSummary[]>([]);
  const [eventsFilter, setEventsFilter] = useState<'all' | 'processed' | 'failed'>('all');
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [healthLoading, setHealthLoading] = useState(false);
  const [testConvId, setTestConvId] = useState('');
  const [testContent, setTestContent] = useState('Mensaje de prueba desde Mati');
  const [testSending, setTestSending] = useState(false);

  const loadEvents = useCallback(async () => {
    if (!channel) {
      return;
    }
    try {
      setLoadingEvents(true);
      const status = eventsFilter === 'all' ? undefined : eventsFilter;
      const data = await chatwootAdminApi.listEvents(channel.id, { status, limit: 50 });
      setEvents(data);
    } catch (error) {
      toast.error((error as Error).message || 'Error al cargar eventos');
    } finally {
      setLoadingEvents(false);
    }
  }, [channel, eventsFilter]);

  useEffect(() => {
    if (open && channel) {
      void loadEvents();
    }
  }, [open, channel, loadEvents]);

  const handleHealthCheck = async () => {
    if (!channel) return;
    try {
      setHealthLoading(true);
      const result = await chatwootAdminApi.healthCheck(channel.id);
      if (result.status === 'ok') {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
      onChannelUpdated();
    } catch (error) {
      toast.error((error as Error).message || 'Error en health check');
    } finally {
      setHealthLoading(false);
    }
  };

  const handleTestMessage = async () => {
    if (!channel) return;
    const externalConversationId = testConvId.trim();
    const content = testContent.trim();
    if (!externalConversationId || !content) {
      toast.error('Completá conversation ID y mensaje');
      return;
    }
    try {
      setTestSending(true);
      const result = await chatwootAdminApi.testMessage(channel.id, {
        externalConversationId,
        content,
      });
      toast.success(`Mensaje enviado (id ${result.messageId})`);
      void loadEvents();
    } catch (error) {
      toast.error((error as Error).message || 'Error al enviar mensaje de prueba');
    } finally {
      setTestSending(false);
    }
  };

  if (!channel) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-4xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {channel.name}{' '}
            <Badge variant='secondary' className='ml-2'>
              {channel.status}
            </Badge>
          </DialogTitle>
          <p className='text-sm text-muted-foreground'>Bot: {botLabel}</p>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='rounded-lg border p-3 space-y-2 text-sm'>
            <p>
              <span className='font-medium'>Base URL:</span> {channel.baseUrl}
            </p>
            <p>
              <span className='font-medium'>Account / Inbox:</span> {channel.accountId} /{' '}
              {channel.inboxId}
            </p>
            <p className='text-xs text-muted-foreground'>
              Token: ••••{channel.apiAccessTokenLast4}
              {channel.hasHmacSignature ? ' · HMAC configurado' : ' · Sin HMAC'}
              {channel.hasAgentBot ? ' · Agent bot' : ''}
            </p>
            {channel.lastHealthCheckAt && (
              <p className='text-xs'>
                Último health check: {new Date(channel.lastHealthCheckAt).toLocaleString()} —{' '}
                {channel.lastHealthCheckStatus ?? 'n/a'}
              </p>
            )}
            <Button
              type='button'
              size='sm'
              variant='outline'
              disabled={healthLoading}
              onClick={() => void handleHealthCheck()}
            >
              <HeartPulse className='h-4 w-4 mr-1' />
              {healthLoading ? 'Verificando…' : 'Health check'}
            </Button>
          </div>

          <div className='rounded-lg border p-3 space-y-2'>
            <p className='text-sm font-medium'>Webhook URL (configurar en Chatwoot)</p>
            <div className='flex gap-2'>
              <code className='flex-1 text-xs break-all bg-muted p-2 rounded'>
                {channel.webhookUrl || 'Configurar CHATWOOT_WEBHOOK_BASE_URL'}
              </code>
              {channel.webhookUrl && (
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => void copyText(channel.webhookUrl, 'Webhook URL')}
                >
                  <Copy className='h-4 w-4' />
                </Button>
              )}
            </div>
            <p className='text-xs text-muted-foreground'>
              Handoff: {channel.handoffOnAssignment ? 'al asignar' : 'manual'}
              {channel.handoffKeyword ? ` · keyword "${channel.handoffKeyword}"` : ''}
              {channel.resumeKeyword ? ` · resume "${channel.resumeKeyword}"` : ''}
            </p>
          </div>

          <div className='rounded-lg border p-3 space-y-3'>
            <p className='text-sm font-medium'>Mensaje de prueba</p>
            <div className='grid gap-3 md:grid-cols-2'>
              <div>
                <Label>Conversation ID (Chatwoot)</Label>
                <Input
                  value={testConvId}
                  onChange={e => setTestConvId(e.target.value)}
                  placeholder='42'
                />
              </div>
              <div>
                <Label>Contenido</Label>
                <Input
                  value={testContent}
                  onChange={e => setTestContent(e.target.value.slice(0, 8000))}
                />
              </div>
            </div>
            <Button
              type='button'
              size='sm'
              disabled={testSending}
              onClick={() => void handleTestMessage()}
            >
              <Send className='h-4 w-4 mr-1' />
              {testSending ? 'Enviando…' : 'Enviar test message'}
            </Button>
          </div>

          <div>
            <div className='flex items-center justify-between mb-2'>
              <p className='text-sm font-medium'>Últimos eventos webhook</p>
              <select
                className='border border-input rounded-md p-1 text-sm bg-background'
                value={eventsFilter}
                onChange={e =>
                  setEventsFilter(e.target.value as 'all' | 'processed' | 'failed')
                }
              >
                <option value='all'>Todos</option>
                <option value='processed'>Procesados</option>
                <option value='failed'>Fallidos</option>
              </select>
            </div>
            {loadingEvents ? (
              <p className='text-sm text-muted-foreground'>Cargando eventos…</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Evento</TableHead>
                    <TableHead>Mensaje</TableHead>
                    <TableHead>Conversación</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Recibido</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className='text-muted-foreground text-sm'>
                        Sin eventos para este filtro.
                      </TableCell>
                    </TableRow>
                  ) : (
                    events.map(event => (
                      <TableRow key={event.id}>
                        <TableCell className='text-xs'>{event.eventName}</TableCell>
                        <TableCell className='font-mono text-xs'>
                          {event.externalMessageId}
                        </TableCell>
                        <TableCell className='font-mono text-xs'>
                          {event.externalConversationId}
                        </TableCell>
                        <TableCell>
                          {event.processedAt ? (
                            <Badge variant='secondary'>OK</Badge>
                          ) : event.errorMessage ? (
                            <Badge variant='destructive'>Error</Badge>
                          ) : (
                            <Badge variant='outline'>Pendiente</Badge>
                          )}
                        </TableCell>
                        <TableCell className='text-xs'>
                          {new Date(event.receivedAt).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
